import { createFileRoute, Link } from "@tanstack/react-router";

import { ThemeMenu } from "../components/ThemeMenu";
import { useCurrentUser } from "../lib/currentUser";

/**
 * Public landing page. Visible to everyone — the wordmark in the
 * signed-in shell points here, so logged-in learners can come back
 * to the marketing/home page at will. The dashboard lives at /home;
 * the hero CTA below switches based on auth state.
 */
export const Route = createFileRoute("/")({
  component: LandingPage,
});

function LandingPage() {
  return (
    <main className="min-h-screen bg-paper">
      <LandingHeader />
      <Hero />
      <Principles />
      <EngineCard />
      <Footer />
    </main>
  );
}

function LandingHeader() {
  const { data: user } = useCurrentUser();
  return (
    <header className="border-b border-ink-100">
      <div className="mx-auto max-w-5xl px-6 sm:px-8 h-14 flex items-center justify-between">
        <Link
          to="/"
          className="font-serif text-xl text-accent hover:text-accent-hover transition-colors"
        >
          lingua
        </Link>
        <div className="flex items-center gap-1">
          <ThemeMenu />
          {user ? (
            <Link
              to="/home"
              className="ml-2 inline-flex items-center gap-1.5 text-sm text-ink-700 hover:text-ink-900 transition-colors"
            >
              Dashboard
              <span aria-hidden className="text-ink-300">
                →
              </span>
            </Link>
          ) : (
            <Link
              to="/login"
              className="ml-2 inline-flex items-center gap-1.5 text-sm text-ink-700 hover:text-ink-900 transition-colors"
            >
              Sign in
              <span aria-hidden className="text-ink-300">
                →
              </span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function Hero() {
  const { data: user } = useCurrentUser();
  const signedIn = !!user;
  return (
    <section className="border-b border-ink-100">
      <div className="mx-auto max-w-5xl px-6 sm:px-8 pt-20 sm:pt-32 pb-20 sm:pb-28">
        <div className="eyebrow mb-6">Lingua · adaptive language learning</div>
        <h1 className="font-serif text-5xl sm:text-7xl text-ink-900 tracking-tight leading-[1.05] mb-8 sm:mb-10 max-w-3xl">
          The language app that's{" "}
          <em className="italic text-ink-700">honest</em> about what you know.
        </h1>
        <p className="text-ink-500 text-lg sm:text-xl leading-relaxed max-w-2xl mb-10 sm:mb-12">
          A Bayesian belief model behind every skill, every word, every
          construction. No streaks, no confetti, no inflated numbers — just a
          calibrated estimate of where you are and what to study next.
        </p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <Link
            to={signedIn ? "/home" : "/login"}
            className="inline-flex items-center gap-2 bg-accent px-6 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
          >
            {signedIn ? "Go to your dashboard" : "Sign in"}
            <span aria-hidden>→</span>
          </Link>
          <Link
            to="/about"
            className="text-ink-700 underline-offset-4 hover:underline"
          >
            Read the engine essay
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Principles ──────────────────────────────────────────────────────────────

function Principles() {
  return (
    <section className="border-b border-ink-100">
      <div className="mx-auto max-w-5xl px-6 sm:px-8 py-16 sm:py-24">
        <div className="eyebrow mb-3">Three principles</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-ink-900 mb-12 sm:mb-16 max-w-2xl">
          What we build differently.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10">
          <Principle
            n="01"
            title="Adaptive by mathematics."
            body={
              <>
                FSRS-5 for the forgetting curve. Item Response Theory for
                question difficulty. A Bayesian posterior over your hidden
                ability, updated on every answer. The engine is a POMDP, not a
                spreadsheet.
              </>
            }
          />
          <Principle
            n="02"
            title="No gamification debt."
            body={
              <>
                Other apps invented streaks to keep you opening the app, then
                spent a decade hiding what you actually knew behind the dopamine
                loop. We measure mastery directly — and tell you the truth.
              </>
            }
          />
          <Principle
            n="03"
            title="Calibrated to a standard."
            body={
              <>
                Progress is reported in CEFR coverage with confidence intervals.
                A "67% to B1" claim means a tested 67%, not a marketing 67%.
                Built originally to clear a Berlin visa deadline.
              </>
            }
          />
        </div>
      </div>
    </section>
  );
}

function Principle({
  n,
  title,
  body,
}: {
  n: string;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <article className="relative">
      <div className="font-serif text-ink-300 text-2xl mb-4 tabular-nums">
        {n}
      </div>
      <h3 className="font-serif text-xl text-ink-900 mb-3 tracking-tight">
        {title}
      </h3>
      <p className="text-ink-500 leading-relaxed text-[15px]">{body}</p>
    </article>
  );
}

// ─── Engine card (pull-quote / spec sheet) ──────────────────────────────────

function EngineCard() {
  return (
    <section className="border-b border-ink-100">
      <div className="mx-auto max-w-5xl px-6 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          <div>
            <div className="eyebrow mb-3">The engine, briefly</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink-900 mb-6 tracking-tight">
              A scheduler with a memory of its own.
            </h2>
            <p className="text-ink-500 leading-relaxed mb-6">
              For every skill, the model carries{" "}
              <em className="italic text-ink-700">stability</em> — how many days
              you'd hold a memory before it decays — and{" "}
              <em className="italic text-ink-700">σ²(θ)</em>, the variance of
              its belief about your ability. The scheduler picks the next
              question that maximizes information gain about you, gated by
              what's due. A right answer grows stability. A wrong one shrinks σ
              — we learn more about you on a miss than on an easy hit.
            </p>
            <Link
              to="/about"
              className="text-ink-900 underline underline-offset-4 hover:text-ink-700"
            >
              Read the full explanation →
            </Link>
          </div>
          <dl className="border-l border-ink-100 pl-6 sm:pl-8 space-y-5 text-[15px]">
            <SpecRow
              term="Memory model"
              value="FSRS-5 (19 calibrated weights)"
            />
            <SpecRow term="Item response" value="2-parameter logistic IRT" />
            <SpecRow
              term="Ability estimate"
              value="Gaussian posterior, Laplace updates"
            />
            <SpecRow
              term="Selection policy"
              value="Boltzmann-softmax over Q̂(b, a)"
            />
            <SpecRow
              term="Mastery threshold"
              value="σ ≤ 0.32 AND μ ≥ logit(0.85)"
            />
            <SpecRow term="Languages today" value="German" />
            <SpecRow
              term="Languages planned"
              value="Italian, Portuguese, Greek, Latin"
            />
          </dl>
        </div>
      </div>
    </section>
  );
}

function SpecRow({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-6">
      <dt className="text-ink-400">{term}</dt>
      <dd className="text-ink-900 text-right font-medium tabular-nums">
        {value}
      </dd>
    </div>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────────────

function Footer() {
  const { data: user } = useCurrentUser();
  return (
    <footer className="mx-auto max-w-5xl px-6 sm:px-8 py-12 sm:py-16">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <div className="font-serif text-ink-900 text-lg mb-1">Lingua</div>
          <div className="text-ink-400 text-sm">
            Built in Berlin. Mathematics first, marketing never.
          </div>
        </div>
        <nav className="flex items-center gap-6 text-sm text-ink-500">
          {user ? (
            <Link to="/home" className="hover:text-ink-900">
              Dashboard
            </Link>
          ) : (
            <Link to="/login" className="hover:text-ink-900">
              Sign in
            </Link>
          )}
          <Link to="/about" className="hover:text-ink-900">
            About
          </Link>
        </nav>
      </div>
    </footer>
  );
}
